import type { JSX } from 'react';
import type { RequestFileResource } from '@majuscule/contracts';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronLeft, ChevronRight, File, FileText, X } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

import { Button } from '@/components/ui/button';
import { internalFetchBlob } from '@/lib/api';

type RequestFiles = { inputs: RequestFileResource[]; outputs: RequestFileResource[] };

const PDF_MIME_TYPE = 'application/pdf';

const tileClass =
  'group flex w-40 flex-col gap-1.5 rounded-md text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background';
const thumbClass =
  'grid h-32 w-40 place-items-center overflow-hidden rounded-md border border-border bg-muted transition-colors group-hover:border-input';
const captionClass = 'truncate font-mono text-[11px] text-muted-foreground';

function isImage(file: RequestFileResource): boolean {
  return file.mimeType.startsWith('image/');
}

function formatSize(bytes: number): string {
  if (bytes < 1024) {
    return `${bytes}\u00a0o`;
  }
  if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toLocaleString('fr-FR', { maximumFractionDigits: 0 })}\u00a0Ko`;
  }
  return `${(bytes / (1024 * 1024)).toLocaleString('fr-FR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}\u00a0Mo`;
}

function fileQueryOptions(fileId: string) {
  return {
    queryKey: ['internal-file', fileId],
    queryFn: () => internalFetchBlob(`/internal/files/${fileId}/content`),
    staleTime: Infinity,
    retry: false,
  };
}

/** Object URL for a file, created once its content is downloaded and revoked on unmount. */
function useFileUrl(fileId: string): { url: string | null; failed: boolean } {
  const query = useQuery(fileQueryOptions(fileId));
  const blob = query.data;
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (blob === undefined) {
      setUrl(null);
      return;
    }
    const objectUrl = URL.createObjectURL(blob);
    setUrl(objectUrl);
    return () => {
      URL.revokeObjectURL(objectUrl);
    };
  }, [blob]);
  return { url, failed: query.isError };
}

function ImageThumbnail({
  file,
  onOpen,
}: {
  file: RequestFileResource;
  onOpen: () => void;
}): JSX.Element {
  const { url, failed } = useFileUrl(file.id);
  return (
    <button type="button" onClick={onOpen} title={file.id} className={tileClass}>
      <span className={thumbClass}>
        {url !== null ? (
          <img src={url} alt={file.id} className="h-full w-full object-cover" />
        ) : (
          <span className="text-caption text-muted-foreground">
            {failed ? 'Indisponible' : 'Chargement…'}
          </span>
        )}
      </span>
      <span className={captionClass}>{file.id}</span>
      <span className="text-[11px] text-muted-foreground">{formatSize(file.sizeBytes)}</span>
    </button>
  );
}

function PdfTile({ file }: { file: RequestFileResource }): JSX.Element {
  const queryClient = useQueryClient();
  const [error, setError] = useState(false);

  // The tab is opened synchronously (inside the click) so the popup blocker lets
  // it through, then pointed at the downloaded content.
  const open = useCallback((): void => {
    const tab = window.open('', '_blank');
    setError(false);
    queryClient
      .query(fileQueryOptions(file.id))
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        if (tab === null) {
          window.open(url, '_blank');
        } else {
          tab.location.href = url;
        }
      })
      .catch(() => {
        tab?.close();
        setError(true);
      });
  }, [file.id, queryClient]);

  return (
    <button type="button" onClick={open} title={file.id} className={tileClass}>
      <span className={thumbClass}>
        <span className="grid justify-items-center gap-1.5 text-center">
          <FileText aria-hidden="true" className="size-6 text-muted-foreground" />
          <span className="text-caption font-medium text-muted-foreground">
            PDF{file.pages !== null ? ` · ${file.pages}\u00a0p.` : ''}
          </span>
        </span>
      </span>
      <span className={captionClass}>{file.id}</span>
      <span className="text-[11px] text-muted-foreground">
        {error ? (
          <span className="text-danger-fg">Ouverture impossible</span>
        ) : (
          formatSize(file.sizeBytes)
        )}
      </span>
    </button>
  );
}

function OtherTile({ file }: { file: RequestFileResource }): JSX.Element {
  return (
    <div className="flex w-40 flex-col gap-1.5">
      <span className={thumbClass}>
        <span className="grid justify-items-center gap-1.5 px-2 text-center">
          <File aria-hidden="true" className="size-6 text-muted-foreground" />
          <span className="break-all font-mono text-[11px] text-muted-foreground">
            {file.mimeType}
          </span>
        </span>
      </span>
      <span className={captionClass}>{file.id}</span>
    </div>
  );
}

/** Full-screen viewer. Arrows only move within the given images (one group at a time). */
function Lightbox({
  images,
  index,
  onIndexChange,
  onClose,
}: {
  images: RequestFileResource[];
  index: number;
  onIndexChange: (index: number) => void;
  onClose: () => void;
}): JSX.Element | null {
  const current = images[index];
  const count = images.length;
  const previous = useCallback(() => {
    onIndexChange((index - 1 + count) % count);
  }, [index, count, onIndexChange]);
  const next = useCallback(() => {
    onIndexChange((index + 1) % count);
  }, [index, count, onIndexChange]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        onClose();
      } else if (event.key === 'ArrowLeft') {
        previous();
      } else if (event.key === 'ArrowRight') {
        next();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [previous, next, onClose]);

  if (current === undefined) {
    return null;
  }
  // The viewer stays dark in both themes (photo viewing), hence the fixed
  // black backdrop and white controls rather than theme tokens.
  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Visionneuse d'images"
      onClick={onClose}
      className="animate-fade fixed inset-0 z-50 flex items-center justify-center bg-black/85 p-4"
    >
      <Button
        type="button"
        variant="ghost"
        size="icon"
        onClick={onClose}
        aria-label="Fermer"
        className="absolute right-4 top-4 rounded-full bg-white/10 text-white hover:bg-white/20 hover:text-white"
      >
        <X aria-hidden="true" />
      </Button>
      {count > 1 ? (
        <>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={(event) => {
              event.stopPropagation();
              previous();
            }}
            aria-label="Image précédente"
            className="absolute left-4 top-1/2 size-10 -translate-y-1/2 rounded-full bg-white/10 text-white hover:bg-white/20 hover:text-white"
          >
            <ChevronLeft aria-hidden="true" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={(event) => {
              event.stopPropagation();
              next();
            }}
            aria-label="Image suivante"
            className="absolute right-4 top-1/2 size-10 -translate-y-1/2 rounded-full bg-white/10 text-white hover:bg-white/20 hover:text-white"
          >
            <ChevronRight aria-hidden="true" />
          </Button>
        </>
      ) : null}
      <LightboxImage file={current} />
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 rounded-full bg-black/60 px-3 py-1 font-mono text-xs text-white">
        {count > 1 ? `${index + 1} / ${count} · ` : ''}
        {current.id}
      </div>
    </div>,
    document.body,
  );
}

function LightboxImage({ file }: { file: RequestFileResource }): JSX.Element {
  const { url, failed } = useFileUrl(file.id);
  if (url === null) {
    return (
      <span className="text-sm text-white/70">{failed ? 'Image indisponible' : 'Chargement…'}</span>
    );
  }
  return (
    <img
      src={url}
      alt={file.id}
      onClick={(event) => {
        event.stopPropagation();
      }}
      className="max-h-[90vh] max-w-[92vw] rounded-lg object-contain"
    />
  );
}

function DocumentGroup({
  title,
  files,
  onOpenImage,
}: {
  title: string;
  files: RequestFileResource[];
  onOpenImage: (images: RequestFileResource[], index: number) => void;
}): JSX.Element | null {
  if (files.length === 0) {
    return null;
  }
  const images = files.filter(isImage);
  return (
    <section className="grid gap-2">
      <h3 className="m-0 text-label text-muted-foreground">
        {title} ({files.length})
      </h3>
      <div className="flex flex-wrap gap-4">
        {files.map((file) => {
          if (isImage(file)) {
            return (
              <ImageThumbnail
                key={file.id}
                file={file}
                onOpen={() => {
                  onOpenImage(
                    images,
                    images.findIndex((image) => image.id === file.id),
                  );
                }}
              />
            );
          }
          return file.mimeType === PDF_MIME_TYPE ? (
            <PdfTile key={file.id} file={file} />
          ) : (
            <OtherTile key={file.id} file={file} />
          );
        })}
      </div>
    </section>
  );
}

/** Number of documents of a request (sent and returned). */
export function requestDocumentCount(files: RequestFiles): number {
  return files.inputs.length + files.outputs.length;
}

/**
 * Documents sent with a request and documents it returned; images open in a
 * viewer, PDFs in a new tab. Renders the groups only (placed by the page, in a
 * tab of the request card); nothing when the request has no document.
 */
export function RequestDocuments({ files }: { files: RequestFiles }): JSX.Element | null {
  const [viewer, setViewer] = useState<{ images: RequestFileResource[]; index: number } | null>(
    null,
  );
  const closeViewer = useCallback(() => {
    setViewer(null);
  }, []);
  const changeIndex = useCallback((index: number) => {
    setViewer((state) => (state === null ? null : { ...state, index }));
  }, []);

  if (requestDocumentCount(files) === 0) {
    return null;
  }
  const open = (images: RequestFileResource[], index: number): void => {
    setViewer({ images, index });
  };
  return (
    <div className="grid gap-6">
      <DocumentGroup title="Documents envoyés" files={files.inputs} onOpenImage={open} />
      <DocumentGroup title="Documents reçus" files={files.outputs} onOpenImage={open} />
      {viewer !== null ? (
        <Lightbox
          images={viewer.images}
          index={viewer.index}
          onIndexChange={changeIndex}
          onClose={closeViewer}
        />
      ) : null}
    </div>
  );
}
